"use client";

import { useState } from "react";
import styles from "./BrandColors.module.css";
import { cmsLinkUrl } from "@/lib/downloadUrl";
import CMSLink from "../common/CMSLink";
import { IconCheck, IconCopy, IconDownload } from "@tabler/icons-react";

// Pick dark or light text depending on swatch luminance.
function isLightColor(hex) {
  const h = (hex || "").replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  if (full.length !== 6) return false;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > 160;
}

function ColorTile({ color, label, isWide }) {
  const [copied, setCopied] = useState(null);
  const light = isLightColor(color.hex);

  const handleCopy = (format, val) => {
    navigator.clipboard?.writeText(val).then(() => {
      setCopied(format);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  return (
    <div
      className={`${styles.tile} ${isWide ? styles.tileWide : ""} ${
        light ? styles.tileLight : ""
      }`}
      style={{ background: color.hex }}
    >
      <div className={styles.tileTop}>
        <span className={styles.tileName}>{label}</span>
      </div>

      <div className={styles.tileValues}>
        <div
          onClick={() => handleCopy("hex", color.hex)}
          style={{
            cursor: "pointer",
            opacity: copied === "hex" ? 0.7 : 1,
            transition: "opacity 0.2s",
          }}
          title="Click to copy HEX"
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
            <div style={{ marginTop: "1px" }}>
              {copied === "hex" ? (
                <IconCheck size={16} />
              ) : (
                <IconCopy size={16} />
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span className={styles.valueLabel}>HEX</span>
              <span className={styles.value}>{color.hex}</span>
            </div>
          </div>
        </div>
        {color.rgb && (
          <div
            onClick={() => handleCopy("rgb", color.rgb)}
            style={{
              cursor: "pointer",
              opacity: copied === "rgb" ? 0.7 : 1,
              transition: "opacity 0.2s",
            }}
            title="Click to copy RGB"
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
              <div style={{ marginTop: "1px" }}>
                {copied === "rgb" ? (
                  <IconCheck size={16} />
                ) : (
                  <IconCopy size={16} />
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span className={styles.valueLabel}>RGB</span>
                <span className={styles.value}>{color.rgb}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function BrandColors({ data }) {
  const {
    section_title,
    section_description,
    cta,
    download_url,
    color_groups = [],
  } = data || {};

  if (!color_groups.length) return null;

  // The admin now saves the button under `cta`; `download_url` is the old field.
  const dlConfig = {
    url: cmsLinkUrl(cta?.url) ? cta.url : download_url,
    filename:
      cta?.filename || data?.download_filename || "tykkit-brand-colors.zip",
    force_download: cta?.force_download ?? true,
  };
  const hasDownload = Boolean(cmsLinkUrl(dlConfig.url));
  const dlLabel = cta?.label || "Download All Assets";

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.leftCol}>
          {section_title && (
            <h2 className={styles.sectionTitle}>{section_title}</h2>
          )}
          {section_description && (
            <p className={styles.description}>{section_description}</p>
          )}
          {hasDownload && (
            <CMSLink config={dlConfig} className={styles.downloadBtn}>
              <IconDownload /> {dlLabel}
            </CMSLink>
          )}
        </div>

        {/* Colour mosaic: primary group full width, other groups share a row */}
        <div className={styles.palette}>
          {color_groups.map((group, gi) => {
            const isPrimary = group.group_label?.toLowerCase() === "primary";
            const colors = group.colors || [];
            return (
              <div
                key={gi}
                className={isPrimary ? styles.rowPrimary : styles.row}
              >
                {colors.map((color, ci) => (
                  <ColorTile
                    key={ci}
                    color={color}
                    isWide={isPrimary}
                    label={
                      isPrimary
                        ? group.group_label
                        : color.name || group.group_label
                    }
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
